import apiClient from './client';

export type OutreachStatus = 'new' | 'drafted' | 'sent' | 'replied' | 'bounced' | 'declined' | 'customer';

export interface OutreachCategory { id: string; label: string; }
export interface OutreachTemplate { id: string; title: string; category?: string; subject?: string; body?: string; }

export interface OutreachContactListItem {
  id: number;
  category?: string;
  name: string;
  phone?: string;
  email?: string;
  website?: string;
  location?: string;
  specialty?: string;
  verified: number;
  status: OutreachStatus;
  last_contacted_at?: string | null;
}

export interface OutreachMessage {
  id: number; channel: string; to_email: string; subject: string; status: string; created_at: string;
}

export interface OutreachContact extends OutreachContactListItem {
  draft_template_id?: string | null;
  draft_greeting?: string | null;
  draft_subject?: string | null;
  draft_body?: string | null;
  notes?: string | null;
  primary_email?: string | null;
  suppressed?: boolean;
  messages: OutreachMessage[];
}

export const getCategories = async (): Promise<OutreachCategory[]> => {
  const { data } = await apiClient.get('/outreach/categories');
  return data.data;
};

export const getSnippets = async (): Promise<Record<string, string>> => {
  const { data } = await apiClient.get('/outreach/snippets');
  return data.data;
};

export const updateSnippet = async (key: string, body: string): Promise<void> => {
  await apiClient.put(`/outreach/snippets/${encodeURIComponent(key)}`, { body });
};

export const getTemplates = async (): Promise<OutreachTemplate[]> => {
  const { data } = await apiClient.get('/outreach/templates');
  return data.data;
};

export const createTemplate = async (t: Partial<OutreachTemplate>): Promise<{ id: string }> => {
  const { data } = await apiClient.post('/outreach/templates', t);
  return data.data;
};

export const deleteTemplate = async (id: string): Promise<void> => {
  await apiClient.delete(`/outreach/templates/${id}`);
};

export const getContacts = async (params?: { category?: string; status?: string; q?: string }): Promise<OutreachContactListItem[]> => {
  const { data } = await apiClient.get('/outreach/contacts', { params });
  return data.data;
};

export const getContact = async (id: number): Promise<OutreachContact> => {
  const { data } = await apiClient.get(`/outreach/contacts/${id}`);
  return data.data;
};

export const updateContact = async (id: number, patch: Partial<OutreachContact>): Promise<void> => {
  await apiClient.put(`/outreach/contacts/${id}`, patch);
};

export const setContactStatus = async (id: number, status: OutreachStatus): Promise<void> => {
  await apiClient.put(`/outreach/contacts/${id}/status`, { status });
};

export const sendOutreach = async (
  id: number,
  payload: { subject: string; body: string; senderName?: string; replyTo?: string },
): Promise<{ to: string }> => {
  const { data } = await apiClient.post(`/outreach/contacts/${id}/send`, payload);
  return data.data;
};

// Record an SMS that was sent client-side via the local ADB bridge.
export const logSms = async (id: number, payload: { to: string; body: string }): Promise<void> => {
  await apiClient.post(`/outreach/contacts/${id}/log-sms`, payload);
};

export interface BatchSendResult {
  sent: number;
  skipped: { id: number; reason: string }[];
  capped?: number;
}
export const sendBatch = async (
  contactIds: number[],
  senderName?: string,
  replyTo?: string,
): Promise<BatchSendResult> => {
  const { data } = await apiClient.post('/outreach/contacts/send-batch', { contactIds, senderName, replyTo });
  return data.data;
};

export interface SuppressionEntry { email: string; reason: string; source?: string; created_at: string; }
export const getSuppression = async (): Promise<SuppressionEntry[]> => {
  const { data } = await apiClient.get('/outreach/suppression');
  return data.data;
};
export const addSuppression = async (email: string): Promise<void> => {
  await apiClient.post('/outreach/suppression', { email });
};
export const removeSuppression = async (email: string): Promise<void> => {
  await apiClient.delete(`/outreach/suppression/${encodeURIComponent(email)}`);
};

export interface AiTemplateResult { kind: string; title: string; subject?: string; body: string; }
export const aiTemplate = async (
  payload: { kind: 'email' | 'sms'; purpose: string; category?: string; tone?: string },
): Promise<AiTemplateResult> => {
  const { data } = await apiClient.post('/outreach/ai/template', payload);
  return data.data;
};

// ── personalization (client-side preview; server re-applies on send) ──────
export function expandOutreach(
  text: string,
  snippets: Record<string, string>,
  senderName: string,
  greeting?: string | null,
): string {
  if (!text) return '';
  let t = text.replace(/\{\{(\w+)\}\}/g, (_m, k) => snippets[k] ?? `{{${k}}}`);
  t = t.replace(/\[Your Name\]/g, senderName || 'the OchrePoint team');
  if (greeting) t = t.replace(/\[[^\]]*Name\]/g, greeting);
  return t;
}
